<template>
    <div id="tradeTrackerTable">

        <div class="border-t-2 border-blue-600">
            <div class="table-header border-b">
                <div class="inline-block w-full sm:w-auto sm:mr-3 print:w-1/4">
                    <select-c label="View:" :options="views" v-model="view" @change="emit('updateView', view)"></select-c>
                </div>

                <div class="inline-block w-full sm:w-auto xs:mt-2 sm:mr-3 print:w-1/4 print:mt-0 print:ml-3">
                    <select-c label="Period:" :options="periods" v-model="period" @change="emit('updatePeriod', period)"></select-c>
                </div>

                <div class="flex flex-wrap w-full lg:w-auto mt-3 ml-auto sm:mt-2 lg:mt-0 border-t border-gray-400 lg:border-t-0 print:hidden">
                    <div class="inline-block ml-auto mr-0 sm:mr-3 xs:w-full mt-2 xs:mb-2 lg:my-0">
                        <label>
                            <select @change="onChangeAddTransaction" class="form-control form-control-sm form-control-fixed form-control-blue-600" v-model="type" aria-label="Add a Transaction">
                                <option value="">Add a Transaction</option>
                                <option :value="types[2]">Futures</option>
                                <option :value="types[10]">Forex</option>
                                <option :value="types[13]">Futures Spread</option>
                                <option :value="types[12]">Options</option>
                                <option :value="types[1]">Stocks & ETFs</option>
                                <option :value="types[18]">Crypto</option>
                            </select>
                        </label>
                    </div>
                    <div class="inline-block mr-2 mt-0 sm:mt-2 xs:mb-2 lg:my-0 print:hidden" v-if="haveItems">
                        <button class="btn btn-sm btn-green" @click="exportToCSV">
                            <i class="fas fa-file-csv"></i>&nbsp;Export
                        </button>
                    </div>
                    <div class="inline-block mt-0 sm:mt-2 xs:mb-2 lg:my-0 print:hidden">
                        <button class="btn btn-sm btn-yellow" @click="editPortfolio">
                            <i class="far fa-edit"></i>&nbsp;Edit This Portfolio
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <alert-box v-if="!haveItems">
            <template #body>
                <div class="text-base leading-tight">
                    No transactions have been added. Please create one by going to the blue dropdown menu "Add a Transaction", above.
                </div>
            </template>
        </alert-box>

        <div class="table-responsive relative" v-else>
            <template v-if="defaultView == 'portfolio'">
                <table ref="containerRef" class="table table-bordered trade-tracker-table">
                    <thead ref="theadRef">
                        <tr>
                            <th>Qty</th>
                            <th>Position</th>
                            <th>Entry Date</th>
                            <th>Entry Price</th>
                            <th>Current</th>
                            <th>Exit Date</th>
                            <th>Exit Price</th>
                            <th>Total Change</th>
                            <th>Commission</th>
                            <th>Profit/Loss</th>
                            <th>As Of</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="portfolioItem in portfolio.items">
                        <tr class="bg-tan-200">
                            <td colspan="11">
                                <div class="flex flex-wrap w-full py-1">
                                    <div class="font-bold">
                                        {{ portfolioItem.contractFullName || portfolioItem.symbol }}
                                        <span class="hidden"> - {{ portfolioItem.underlyingFuture || '*' }}</span>
                                    </div>
                                    <div class="ml-auto print:hidden">
                                        <div class="inline-block mr-1" v-if="buttons[portfolioItem.symbolType] && isValidSymbol(portfolioItem.symbol)">
                                            <button-group
                                                :symbol="portfolioItem.symbol"
                                                :underlying-future="portfolioItem.underlyingFuture || null"
                                                :groups="buttons[portfolioItem.symbolType]['value']"
                                                :extra-data="portfolioItem.raw"
                                            >
                                            </button-group>
                                        </div>
                                        <span @click="onEditTransaction(portfolioItem)" class="btn btn-shadow-none btn-blue-ghost btn-xs ml-1 mr-1" title="Edit Transaction"><i class="far fa-edit"></i></span>
                                        <span v-if="portfolios.length > 1" @click="transactionMoveModal = portfolioItem" class="btn btn-shadow-none btn-blue-ghost btn-xs mr-1" title="Move Transaction"><i class="fas fa-exchange-alt"></i></span>
                                        <span @click="transactionRemoveModal = portfolioItem" class="btn btn-shadow-none btn-red-ghost btn-xs" title="Delete Transaction"><i class="fas fa-times"></i></span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr class="bg-white">
                            <td>{{ portfolioItem.quantity || '-' }}</td>
                            <td>{{ portfolioItem.action || '-' }}</td>
                            <td>{{ portfolioItem.entry_date || '-' }}</td>
                            <td>{{ portfolioItem.entry_price || '-' }}</td>
                            <td :class="{'text-blue-400':getItemField(portfolioItem, 'tradeTime')=='Liquidated'}">{{ getItemField(portfolioItem, 'lastPrice') || '-' }}</td>
                            <td>{{ portfolioItem.exit_date || '-' }}</td>
                            <td>{{ portfolioItem.exit_price || '-' }}</td>
                            <td :class="getClass(portfolioItem.raw.total_price_change)">{{ portfolioItem.total_price_change || '-' }}</td>
                            <td class="font-bold">{{ toCurrency(portfolioItem.commission_total) }}</td>
                            <td :class="getClass(portfolioItem.raw.return)" class="font-bold">{{ portfolioItem.return || '-' }}</td>
                            <td :class="{'text-blue-400':getItemField(portfolioItem, 'tradeTime')=='Liquidated'}">{{ getItemField(portfolioItem, 'tradeTime') || '-' }}</td>
                        </tr>
                        <tr class="bg-gray-super-100" v-if="portfolioItem.notes">
                            <td colspan="11" class="portfolio-item-notes">
                                <em>Note: {{ portfolioItem.notes }}</em>
                            </td>
                        </tr>
                        </template>
                    </tbody>
                    <tfoot>
                        <tr>
                            <th>Qty</th>
                            <th>Position</th>
                            <th>Entry Date</th>
                            <th>Entry Price</th>
                            <th>Current</th>
                            <th>Exit Date</th>
                            <th>Exit Price</th>
                            <th>Total Change</th>
                            <th>Commission</th>
                            <th>Profit/Loss</th>
                            <th>As Of</th>
                        </tr>
                    </tfoot>
                </table>
            </template>

            <template v-else>
                <table ref="containerRef" class="table table-striped table-hover border trade-tracker-table-quotes">
                    <thead ref="theadRef">
                        <tr>
                            <th>Name</th>
                            <th>&nbsp;</th>
                            <th>Open</th>
                            <th>High</th>
                            <th>Low</th>
                            <th>Current</th>
                            <th>Change</th>
                            <th>Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="portfolioItem in portfolio.items">
                        <tr>
                            <td>{{ portfolioItem.contractFullName || portfolioItem.symbol }}</td>
                            <td>
                                <div class="float-left inline-block" v-if="buttons[portfolioItem.symbolType] && isValidSymbol(portfolioItem.symbol)">
                                    <button-group
                                        :symbol="portfolioItem.symbol"
                                        :underlying-future="portfolioItem.underlyingFuture || null"
                                        :groups="buttons[portfolioItem.symbolType]['value']"
                                    >
                                    </button-group>
                                </div>
                            </td>
                            <td>{{ getItemField(portfolioItem, 'openPrice') || '-' }}</td>
                            <td>{{ getItemField(portfolioItem, 'highPrice') || '-' }}</td>
                            <td>{{ getItemField(portfolioItem, 'lowPrice') || '-' }}</td>
                            <td>{{ getItemField(portfolioItem, 'lastPrice') || '-' }}</td>
                            <td :class="getItemFieldClass(portfolioItem, 'priceChange')">{{ getItemField(portfolioItem, 'priceChange') || '-' }}</td>
                            <td :class="{'text-blue-400':getItemField(portfolioItem, 'tradeTime')=='Liquidated'}">{{ getItemField(portfolioItem, 'tradeTime') || '-' }}</td>
                        </tr>
                        </template>
                    </tbody>
                    <tfoot>
                        <tr class="text-center">
                            <th>Name</th>
                            <th>&nbsp;</th>
                            <th>Open</th>
                            <th>High</th>
                            <th>Low</th>
                            <th>Current</th>
                            <th>Change</th>
                            <th>Time</th>
                        </tr>
                    </tfoot>
                </table>
            </template>
        </div>

        <div class="w-full mt-4 mb-3">
            <div class="border-t-2 border-blue-600">
                <div class="border bg-tan-200 px-3 py-2">
                    <notes label="Notes:" placeholder="Enter notes..." :initial-note="portfolio.portfolio_notes" v-model="portfolio_notes" @apply="saveNotes" />
                    <div class="text-gray-500 text-xs -mt-1">
                        <em>Changes to notes are saved automatically.</em>
                    </div>
                </div>
            </div>
        </div>

        <modal @close="transactionMoveModal = null" v-if="transactionMoveModal" className="max-w-sm w-full">
            <template #header>
                <span>Move Transaction</span>
            </template>

            <template #body>
                <div class="py-2">
                    <div v-if="!transactionMoveModal.symbol">
                        Error! No symbol!
                    </div>
                    <div class="flex flex-wrap" v-else>
                        <div class="w-full bg-tan-200 text-center py-2 px-1 border mb-2 border-gray-200">
                            <p class="text-md font-bold leading-none text-blue-500">{{ transactionMoveModal.contractFullName || transactionMoveModal.symbolName || transactionMoveModal.symbol }}</p>
                        </div>

                        <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="transactionMoveModal.formError">
                            {{ transactionMoveModal.formError }}
                        </div>

                        <div class="w-full">
                            <label for="selectedPortfolio">Move To Portfolio</label>
                            <select @focus="transactionMoveError=''" v-model="transactionMovePortfolio" class="form-control form-control-sm form-input-modal" id="selectedPortfolio">
                                <option value="">Select Portfolio....</option>
                                <option :value="item" v-for="item in portfolios" :disabled="item.id == portfolio.id">{{ item.caption }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="transactionMoveError">
                        {{ transactionMoveError }}
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="transactionMoveModal = null">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="moveTransaction(transactionMoveModal)">Move</div>
                </div>
            </template>
        </modal>

        <modal @close="transactionRemoveModal = null" v-if="transactionRemoveModal" className="max-w-sm w-full">
            <template #header>
                <div class="text-red-500">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Delete Transaction
                </div>
            </template>
            <template #body>
                <div class="pt-4 pb-2">
                    <div class="mb-3">
                        <p>Are you sure you want to delete the transaction:</p>
                        <p><strong>{{ transactionRemoveModal.contractFullName }}</strong>?</p>
                        <p class="mt-2">Changes can not be undone.</p>
                    </div>
                    <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="transactionRemoveError">
                        {{ transactionRemoveError }}
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-red-ghost inline-flex mx-1" @click="transactionRemoveModal = null">Cancel</div>
                    <div class="btn btn-sm btn-red inline-flex mx-1" @click="removeTransaction(transactionRemoveModal)">Delete</div>
                </div>
            </template>
        </modal>

        <transaction-modal
            v-if="transactionDataModal"
            @updatedTransaction="updatedTransaction"
            @close="transactionDataModal = null"
            :section="transactionDataModal.section"
            :label="transactionDataModal.label"
            :portfolio-id="portfolio.id"
            :portfolio-adjust-cash="portfolio.adjust_cash"
            :transaction-item="transactionItem"
            :transaction-symbol="transactionSymbol"
            :action="transactionAction"
        />

    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Notes from '../../Notes';
import TransactionModal from './TransactionModal';
import { useStickyHeader } from '../../DataTable/mixins/StickTableHeader';

const props = defineProps({
    portfolio: Object,
    portfolios: Array,
    buttons: Object,
    types: Object,
    defaultView: String,
    defaultPeriod: String,
});

const { containerRef, theadRef, handleScroll, resizeHeader } = useStickyHeader();

const emit = defineEmits(['updateView', 'updatePeriod', 'reloadPortfolio', 'editPortfolio', 'saveNotes']);

const view = ref(props.defaultView);
const period = ref(props.defaultPeriod);

const views = ref([
    { label: 'Portfolio', value: 'portfolio', default: props.defaultView === 'portfolio' },
    { label: 'Quote Board', value: 'main', default: props.defaultView === 'main' },
]);
const periods = ref([
    { label: 'Intraday', value: 'intraday', default: props.defaultPeriod === 'intraday' },
    { label: 'End-of-Day', value: 'eod', default: props.defaultPeriod === 'eod' },
]);

const type = ref('');
const portfolio_notes = ref('');

const transactionRemoveModal = ref(null);
const transactionRemoveError = ref('');
const transactionMoveModal = ref(null);
const transactionMoveError = ref('');
const transactionMovePortfolio = ref('');

const transactionDataModal = ref(null);
const transactionAction = ref('add');
const transactionItem = ref(null);
const transactionSymbol = ref(null);

const haveItems = computed(() => {
    return props.portfolio.items && props.portfolio.items.length > 0;
});

const exportToCSV = () => {
    // Helper function to quote fields containing special characters
    const quoteField = (field) => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
            return `"${field.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
        }
        return field;
    };

    const isPortfolioView = view.value === 'portfolio';
    const sPeriod = period.value === 'eod' ? 'End-of-Day' : 'Intraday';

    // CSV Header
    const headers = isPortfolioView
        ? [
            '', 'Qty', 'Position', 'Entry Date', 'Entry Price', 'Current',
            'Exit Date', 'Exit Price', 'Total Change', 'Commission',
            'Profit/Loss', 'As Of'
        ]
        : [
            'Name', 'Open', 'High', 'Low', 'Current', 'Change', 'Time'
        ];

    // Prepare rows for CSV export
    const rows = [];

    props.portfolio.items.forEach((item) => {
        if (isPortfolioView) {
            // For Portfolio View
            // Combine description and notes with a newline, properly quoted
            const description = item.contractFullName || item.symbol;
            const descriptionWithNotes = item.notes ? `${description}\nNote: ${item.notes}` : description;
            const descriptionRow = [quoteField(descriptionWithNotes)];
            rows.push(descriptionRow);

            // Row for main data
            const mainRow = [
                '',
                quoteField(item.quantity || '-'),
                quoteField(item.action || '-'),
                quoteField(item.entry_date || '-'),
                quoteField(item.entry_price || '-'),
                quoteField(getItemField(item, 'lastPrice') || '-'),
                quoteField(item.exit_date || '-'),
                quoteField(item.exit_price || '-'),
                quoteField(item.total_price_change || '-'),
                quoteField(toCurrency(item.commission_total)),
                quoteField(item.return || '-'),
                quoteField(getItemField(item, 'tradeTime') || '-')
            ];
            rows.push(mainRow);

        } else {
            // For Other View
            const mainRow = [
                quoteField(item.contractFullName || item.symbol),
                quoteField(getItemField(item, 'openPrice') || '-'),
                quoteField(getItemField(item, 'highPrice') || '-'),
                quoteField(getItemField(item, 'lowPrice') || '-'),
                quoteField(getItemField(item, 'lastPrice') || '-'),
                quoteField(getItemField(item, 'priceChange') || '-'),
                quoteField(getItemField(item, 'tradeTime') || '-')
            ];
            rows.push(mainRow);
        }
    });

    // Combine headers and rows into CSV format
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");

    // Create a Blob for the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a link to download the CSV file
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Trade Tracker ${(isPortfolioView) ? 'Portfolio' : 'Quote Board'} (${sPeriod}) ${props.portfolio.caption}.csv`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const toCurrency = (value) => {
    if (typeof value !== "number") {
        return value || '-';
    }
    var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });
    return formatter.format(value);
};

const isValidSymbol = (symbol) => {
    let valid = true;
    if (props.portfolio.errors && props.portfolio.errors.invalidSymbols) {
        props.portfolio.errors.invalidSymbols.map((psym) => {
            if (psym == symbol) {
                valid = false;
            }
        });
    }
    return valid;
};

const getItemFieldClass = (item, field) => {
    if (period.value == 'eod') {
        return getClass(item['raw']['daily' + field.charAt(0).toUpperCase() + field.slice(1)]);
    } else {
        return getClass(item['raw'][field]);
    }
};

const getClass = (value) => {
    if (typeof (value) === 'string') {
        value = value.replace(/\$|,/g, '');
    }
    if (value > 0) {
        return 'text-up';
    } else if (value < 0) {
        return 'text-down';
    } else if (value == 0) {
        return 'text-unch';
    }
};

// get portfolio item field depends on 'Period'
const getItemField = (item, field) => {
    if (period.value == 'eod') {
        return item['daily' + field.charAt(0).toUpperCase() + field.slice(1)];
    } else {
        return item[field];
    }
};

// show the add transaction modal
const onChangeAddTransaction = () => {
    transactionAction.value = 'add';
    transactionItem.value = null;
    transactionSymbol.value = null;
    transactionDataModal.value = type.value;
    type.value = '';
};

// show the edit transaction modal
const onEditTransaction = (item) => {
    let editType = props.types[item.symbolType];

    if (editType) {
        transactionAction.value = 'edit';
        transactionDataModal.value = editType;
        transactionItem.value = item;
        transactionSymbol.value = null;
    } else {
        transactionAction.value = 'add';
        transactionDataModal.value = props.types[2];
        transactionItem.value = null;
        transactionSymbol.value = null;
    }
};

// remove the transaction
const removeTransaction = (item) => {
    const params = {
        portfolioId: props.portfolio.id,
        portfolioItemId: item.id,
    };

    const route = window.route('api.trade-tracker.remove-item');
    axios.post(route, params)
        .then((response) => {
            transactionRemoveModal .value= null;
            emit('reloadPortfolio');
        }).catch((error) => {
            if (error && error.response.data.message) {
                transactionRemoveError.value = error.response.data.message;
            } else {
                transactionRemoveError.value = 'Error deleting. Something went wrong, please try again later';
            }
            console.error(error);
        });
};

// move the transaction
const moveTransaction = (item) => {
    if (transactionMovePortfolio.value === '') {
        transactionMoveError.value = 'Please select the portfolio and try again';
        return;
    }

    const params = {
        portfolioId: props.portfolio.id,
        portfolioItemId: item.id,
        portfolioNewId: transactionMovePortfolio.value.id,
        portfolioAdjustCash: transactionMovePortfolio.value.adjust_cash,
    };

    const route = window.route('api.trade-tracker.transfer-item');
    axios.post(route, params)
        .then((response) => {
            transactionMoveModal.value = null;
            emit('reloadPortfolio');
        }).catch((error) => {
            if (error && error.response.data.message) {
                transactionMoveError.value = error.response.data.message;
            } else {
                transactionMoveError.value = 'Error moving. Something went wrong, please try again later';
            }
            console.error(error);
        });
};

// add/edit a portfolio transaction item
const updatedTransaction = (data) => {
    transactionDataModal.value = null;
    transactionItem.value = null;
    transactionSymbol.value = null;
    emit('reloadPortfolio');
};

// emit form to edit the portfolio
const editPortfolio = () => {
    emit('editPortfolio', props.portfolio.id);
};

// save portfolio note
const saveNotes = () => {
    emit('saveNotes', portfolio_notes.value);
};

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', resizeHeader);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', resizeHeader);
});
</script>


<style lang="scss">
#tradeTrackerTable {
    .trade-tracker-table {
        &.table {
            thead,
            tfoot {
                td, th {
                    @apply px-3 py-2 text-center;
                }
            }
            thead {
                tr {
                    @apply border-t;
                }
            }
            tbody {
                td, th {
                    @apply px-3;
                    &:not(.portfolio-item-notes) {
                        @apply text-center;
                    }
                }
            }
            .sortable {
                @apply cursor-pointer;
            }
        }
    }

    .trade-tracker-table-quotes {
        &.table {
            thead,
            tfoot {
                td, th {
                    @apply px-3 py-2;
                }
            }
            thead {
                tr {
                    @apply border-t;
                    td, th {
                    }
                }
            }
            tbody {
                td, th {
                    @apply px-3
                }
            }

            td, th {
                @apply px-3 text-right;
                &:first-child {
                    @apply text-left;
                }
            }

            .sortable {
                @apply cursor-pointer;
            }
        }
    }
}
</style>
